// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { UserService } from '../../user.service';
import { SystemOverviewService } from '../../systemOverview.service';
import {
  ScreenLayoutService,
  ScreenSize,
} from '../../../../shared/services/screen-layout-service/screen-layout.service';
import { combineLatest, map } from 'rxjs';

interface SystemUsageProperties {
  serialNumber: string;
  materialNumber: number;
  deviceType: string;
  modalityId: number;
  modalityName: string;
  productMarketingName: string;
}


type EventProperties = { [key: string]: string | number | boolean };

@Injectable()
export class ApplicationInsightsService {
  appInsights!: ApplicationInsights;
  userEmailHash = '';
  userDepartment!: string;
  systemUsageProperties: SystemUsageProperties;
  systemDetails$ = this.systemOverviewService.systemDetails.lastSuccessfulData$;

  constructor(
    private userService: UserService,
    private readonly screenLayoutService: ScreenLayoutService,
    private readonly systemOverviewService: SystemOverviewService,
  ) {
    this.systemUsageProperties = {
      serialNumber: '',
      materialNumber: 0,
      deviceType: this.getDeviceType(),
      modalityId: 0,
      modalityName: '',
      productMarketingName: '',
    };
    this.getDetailsForTrackEvent();
  }

  private getDetailsForTrackEvent(): void {
    this.initializeUserEmailHash();
    this.getSystemDetailsAndTrackEvent();
    this.initializeUserDepartment();
  }

  private initializeUserEmailHash(): void {
    this.userService.mailId$.subscribe((emailId) => {
      if (emailId) {
        this.hashEmail(emailId).then((hashedEmail) => {
          this.userEmailHash = hashedEmail;
        });
      }
    });
  }

  private initializeUserDepartment(): void {
    this.userService.userData$.subscribe((userData) => {
      if (userData?.department) {
        this.userDepartment = userData.department;
      }
    });
  }

  private getSystemDetailsAndTrackEvent(): void {
    this.systemDetails$.subscribe((systemDetails) => {
      if (systemDetails) {
        this.systemUsageProperties.serialNumber =
          systemDetails.systemSerialNumber;
        this.systemUsageProperties.materialNumber =
          systemDetails.systemMaterialNumber;
        this.systemUsageProperties.modalityId = systemDetails.modalityId;
        this.getModalityName();
        this.systemUsageProperties.productMarketingName =
          systemDetails.systemName;
      }
    });
  }

  modalityName$ = combineLatest([
    this.systemDetails$,
    this.systemOverviewService.modalities.lastSuccessfulData$,
  ]).pipe(
    map(([systemDetails, modalities]) => {
      return modalities.find(({ id }) => id === systemDetails?.modalityId)
        ?.displayName;
    }),
  );

  private getModalityName(): void {
    this.modalityName$.subscribe((modalityName) => {
      if (modalityName) {
        this.systemUsageProperties.modalityName =
          modalityName.toLocaleLowerCase();
      }
    });
  }
  getDeviceType(): string {
    const size = this.screenLayoutService.layout$.value.size;
    switch (size) {
      case ScreenSize.SCREEN_DESKTOP_LARGE:
        return 'Desktop Large';
      case ScreenSize.SCREEN_DESKTOP:
        return 'Desktop';
      case ScreenSize.SCREEN_TABLET:
        return 'Tablet';
      case ScreenSize.SCREEN_PHONE:
        return 'Phone';
      default:
        return 'Unknown';
    }
  }

  initializeApplicationInsights(connectionString?: string): boolean {
    if (connectionString) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          enableAutoRouteTracking: true, // optional: auto-log all route changes
        },
      });
      this.appInsights.loadAppInsights();
    }
    return true;
  }

  //Use this to track events along with hashed user email
  trackUserEvent(eventName: string, properties?: EventProperties): void {
    if (this.appInsights) {
      const trackingProps = {
        email: this.userEmailHash || 'unknown',
        ...properties,
      };
      this.appInsights.trackEvent({ name: eventName }, trackingProps);
    } else {
      console.error('ApplicationInsights is not initialized.');
    }
  }

  trackComponentUsageEvent(
    eventName: string,
    properties?: EventProperties,
  ): void {
    if (this.appInsights) {
      const trackingProps = {
        ...this.systemUsageProperties,
        ...properties,
        email: this.userEmailHash || 'unknown',
        userDepartment: this.userDepartment,
      };
      this.appInsights.trackEvent({ name: eventName }, trackingProps);
    } else {
      console.error('ApplicationInsights is not initialized.');
    }
  }

  trackUsefulApplicationEvent(eventName: string, properties?: EventProperties): void {
    if (this.appInsights) {
      const trackingProps = {
        ...properties,
        deviceType: this.getDeviceType(),
        userDepartment: this.userDepartment,
      };
      this.appInsights.trackEvent({ name: eventName }, trackingProps);
    } else {
      console.error('ApplicationInsights is not initialized.');
    }
  }

  // Use this to track general events without user email
  trackEvent(eventName: string, properties?: EventProperties): void {
    if (this.appInsights) {
      this.appInsights.trackEvent({ name: eventName }, properties);
    } else {
      console.error('ApplicationInsights is not initialized.');
    }
  }

  trackTrace(message: string): void {
    if (this.appInsights) {
      this.appInsights.trackTrace({ message });
    } else {
      console.error('ApplicationInsights is not initialized.');
    }
  }

  logException(exception: Error, severityLevel?: number): void {
    this.appInsights?.trackException({
      exception: exception,
      severityLevel: severityLevel,
    });
  }

  private async hashEmail(email: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(email.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }
}
